import APIAccessType from 'common/IAPIAccessType';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { Box } from '@tanium/react-box';
import { Form, Formik, FormikHelpers } from 'formik';
import { Stack } from '@tanium/react-stack';
import { Button } from '@tanium/react-button';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import Page from 'components/base_page/Page';
import { CreateRequest, RequestTargetType, RequestAccessInput } from 'gql/Request';
import CreateRequestFields from 'components/pages/request/requestPartials/CreateRequestFields';
import RequestSelectors from 'components/pages/request/store/RequestSelectors';
import RequestSlice from 'components/pages/request/store/RequestSlice';
import Spinner from 'components/common/spinner/Spinner';
import IAccessTypes from 'common/IAccessTypes';
import FedBanner from 'components/common/banner/FedBanner';
import Helper from 'utils/Helper';
import { RECENT_REQUESTS_KEY } from 'components/common/requestCard/RecentRequests';

// TODO remove this - handle N access types on the create request form
export interface CreateRequestForm {
  targetType: RequestTargetType;
  targetName?: string | undefined;
  targetId: string;
  ticketInfo?: string;
  details: string;
  accessTypes: RequestAccessInput[];
  accessType: IAccessTypes;
  targetAccessID?: string;
  autopage: boolean;
  autoApprove?: boolean;
  apiAccessType?: APIAccessType;
  namespace?: string;
  role?: string;
}
export const ACCESS_TYPES = 'accessTypes';
export const ROLE_ERROR_MESSAGE = yup.string().label(i18next.t('ROLE')).required(i18next.t('ROLE_REQUIRED_ERROR'));
export const TARGET_ACCESS_ID_ERROR_MESSAGE = yup
  .string()
  .label(i18next.t('TARGET_ACCESS_ID'))
  .required(i18next.t('TARGET_ACCESS_REQUIRE'));
export const CLUSTER_ROLE_ERROR_MESSAGE = yup
  .string()
  .label(i18next.t('CLUSTER_ROLE'))
  .required(i18next.t('CLUSTER_ROLE_REQUIRED_ERROR'));
export const NAMESPACE_ERROR_MESSAGE = yup
  .string()
  .label(i18next.t('NAMESPACE'))
  .required(i18next.t('NAMESPACE_REQUIRED_ERROR'));
export const BASTION_TYPE_ERROR_MESSAGE = yup
  .string()
  .label(i18next.t('BASTION_TYPE'))
  .required(i18next.t('BASTION_TYPE_REQUIRED_ERROR'));
export const API_ACCESS_TYPE_ERROR_MESSAGE = yup
  .string()
  .label(i18next.t('API_ACCESS_TYPE'))
  .required(i18next.t('API_ACCESS_TYPE_REQUIRED_ERROR'));
export const validationSchema = yup.object().shape({
  targetType: yup.string().label(i18next.t('ENVIRONMENT_TYPE')).required(),
  targetName: yup.string().label(i18next.t('TARGET_NAME')).nullable(),
  targetId: yup.string().label(i18next.t('TARGET_ID')).required(),
  accessType: yup.string().when('accessTypes', {
    is: (accessTypes: RequestAccessInput[]) => accessTypes && accessTypes.length,
    then: yup.string().label(i18next.t('ACCESS_TYPE')),
    otherwise: yup.string().label(i18next.t('ACCESS_TYPE')).required(),
  }),
  accessTypes: yup.array().label(i18next.t('ACCESS_TYPES')).min(1, i18next.t('ACCESS_TYPES_MIN_1')).required(),
  role: yup.string().when('accessType', {
    is: (accessType: IAccessTypes) => accessType === IAccessTypes.K8S_API,
    then: ROLE_ERROR_MESSAGE,
    otherwise: yup.string().label(i18next.t('ROLE')),
  }),
  targetAccessID: yup.string().when('accessType', {
    is: (accessType: IAccessTypes) => accessType === IAccessTypes.AWS_ROLE || accessType === IAccessTypes.HOST,
    then: ROLE_ERROR_MESSAGE,
    otherwise: yup.string().label(i18next.t('ROLE')),
  }),
  clusterRole: yup.string().when('accessType', {
    is: (accessType: IAccessTypes) => accessType === IAccessTypes.K8S_API,
    then: CLUSTER_ROLE_ERROR_MESSAGE,
    otherwise: yup.string().label(i18next.t('CLUSTER_ROLE')),
  }),
  namespace: yup.string().when('accessType', {
    is: (accessType: IAccessTypes) => accessType === IAccessTypes.K8S_API,
    then: NAMESPACE_ERROR_MESSAGE,
    otherwise: yup.string().label(i18next.t('NAMESPACE')),
  }),
  bastionType: yup.string().when('accessType', {
    is: (accessType: IAccessTypes) => accessType === IAccessTypes.BASTION,
    then: BASTION_TYPE_ERROR_MESSAGE,
    otherwise: yup.string().label(i18next.t('BASTION_TYPE')),
  }),
  apiAccessType: yup.string().when('accessType', {
    is: (accessType: IAccessTypes) => accessType === IAccessTypes.K8S_API,
    then: API_ACCESS_TYPE_ERROR_MESSAGE,
    otherwise: yup.string().label(i18next.t('API_ACCESS_TYPE')),
  }),
  ticketInfo: yup.string().when('autoApprove', {
    is: (autoApprove?: boolean) => autoApprove === false,
    then: yup.string().label(i18next.t('TICKET_INFO')).max(500).required(),
    otherwise: yup.string().label(i18next.t('TICKET_INFO')).max(500),
  }),
  details: yup.string().label(i18next.t('JUSTIFICATION')).max(500).required(),
  autopage: yup.boolean().required(),
});

export const EMPTY_FORM_VALUES = Object.freeze({
  targetId: undefined,
  targetName: undefined,
  targetType: undefined,
  accessType: undefined,
  accessTypes: [],
  details: undefined,
  ticketInfo: undefined,
  role: undefined,
  clusterRole: undefined,
  namespace: undefined,
  apiAccessType: undefined,
  autopage: false,
  autoApprove: undefined,
}) as unknown as CreateRequestForm;

// This page manages the form to create a single request
const CreateRequestPage: React.VFC = () => {
  const dispatch = useDispatch();
  const createProcessing = useSelector(RequestSelectors.selectCreateProcessing);
  const { t } = useTranslation();

  // Get any needed metadata for this page on load
  useEffect(() => {
    dispatch(RequestSlice.actions.fetchConfigs());
  }, [dispatch]);

  // On page unmount, clear the state
  useEffect(() => {
    return () => {
      dispatch(RequestSlice.actions.clearRequestState());
    };
  }, [dispatch]);

  // Save recent request to local storage
  /* istanbul ignore next */
  const saveRecentRequest = (createRequest: Partial<CreateRequest> & { targetName: string | undefined }): void => {
    const { localStorage } = window;
    let recentRequestsArray = Helper.safelyParseJSON(localStorage.getItem(RECENT_REQUESTS_KEY));
    if (Array.isArray(recentRequestsArray)) {
      // limit the recent requests to 5 items max
      recentRequestsArray = recentRequestsArray.slice(0, 4);
      recentRequestsArray.unshift(createRequest);
    } else {
      recentRequestsArray = [createRequest];
    }
    localStorage.setItem(RECENT_REQUESTS_KEY, JSON.stringify(recentRequestsArray));
  };

  // If the form is submit, dispatch the event and process
  const handleSubmit = (values: CreateRequestForm, formikHelpers: FormikHelpers<CreateRequestForm>): void => {
    /* istanbul ignore next */
    if (values.accessType || values.targetAccessID) {
      formikHelpers.setFieldError(ACCESS_TYPES, t('INCOMPLETE_ACCESS_TYPE'));
    } else {
      /* istanbul ignore next */
      const createRequest: CreateRequest = {
        targetType: values.targetType,
        targetId: values.targetId,
        ticketInfo: values.ticketInfo,
        details: values.details,
        access: values.accessTypes,
        autopage: values.autopage,
      };
      saveRecentRequest({
        targetType: values.targetType,
        targetId: values.targetId,
        access: values.accessTypes,
        targetName: values.targetName,
      });
      /* istanbul ignore next */
      dispatch(RequestSlice.actions.createNewRequest(createRequest));
    }
  };

  // If the form is canceled, navigate back
  const handleCancel = (): void => {
    dispatch(RequestSlice.actions.cancelCreateNewRequest());
  };

  return (
    <Page
      title={t('CREATE_REQUEST')}
      breadcrumbs={[{ href: '/?tab=active', label: t('REQUESTS') }]}
      testId="create-request-page"
    >
      {createProcessing ? (
        <Spinner />
      ) : (
        <Box>
          <FedBanner />
          <Formik
            initialValues={EMPTY_FORM_VALUES}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            enableReinitialize={true}
          >
            {({ submitForm, resetForm }): React.ReactElement => (
              <Form>
                <div data-testid="create-request-form">
                  <CreateRequestFields />
                  <Stack inline itemSpacing={1} mb={2}>
                    <Button data-testid="submit" type="submit" onClick={submitForm}>
                      {t('CREATE')}
                    </Button>
                    <Button
                      data-testid="cancel"
                      variant="secondary"
                      onClick={(): void => {
                        resetForm();
                        handleCancel();
                      }}
                    >
                      {t('CANCEL')}
                    </Button>
                  </Stack>
                </div>
              </Form>
            )}
          </Formik>
        </Box>
      )}
    </Page>
  );
};

export default CreateRequestPage;
